import type { Theme } from "@mui/system";
import type {
    GridColDef,
    GridColumnHeaderParams,
    GridRenderCellParams,
} from "@mui/x-data-grid";
import { GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid";
import {
    Box,
    Checkbox,
    Tooltip,
    useMediaQuery,
    useTheme,
} from "@vapor/react-material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { RowModel } from "./cellRenderers";
import {
    renderActionsCell,
    renderCell,
    renderDateCell,
    renderHeader,
    renderStatus,
} from "./cellRenderers";
import { useRowActionsMenu } from "./useRowActionsMenu";

interface UseColumnsParams {
    onClickTaxSubmission: (taxSubmissionId: string) => unknown;
    onClickRevokeTaxSubmission: (taxSubmissionId: string) => unknown;
    onClickPreviewSogeiOutcome: (content: string, loading: boolean) => unknown;
    setDeleteTaxSubmissionId: React.Dispatch<
        React.SetStateAction<string | undefined>
    >;
    setRejectedTaxSubmissionId: React.Dispatch<
        React.SetStateAction<string | undefined>
    >;
    setUpdateAgencyDataTaxSubmissionId: React.Dispatch<
        React.SetStateAction<string | undefined>
    >;
}

export const useColumns = ({
    onClickTaxSubmission,
    onClickRevokeTaxSubmission,
    onClickPreviewSogeiOutcome,
    setDeleteTaxSubmissionId,
    setRejectedTaxSubmissionId,
    setUpdateAgencyDataTaxSubmissionId,
}: UseColumnsParams): GridColDef[] => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isXlScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.up(theme.breakpoints.values.xl),
    );

    const getRowActions = useRowActionsMenu({
        onClickRevokeTaxSubmission,
        onClickPreviewSogeiOutcome,
        setDeleteTaxSubmissionId,
        setRejectedTaxSubmissionId,
        setUpdateAgencyDataTaxSubmissionId,
    });

    return useMemo<GridColDef[]>(
        () => [
            {
                ...GRID_CHECKBOX_SELECTION_COL_DEF,
                renderCell: (params) => {
                    const isRowSelected = params.api.isRowSelected(
                        params.row.id,
                    );
                    const isRowSelectable = params.api.isRowSelectable(
                        params.row.id,
                    );

                    return !isRowSelectable ? (
                        <Tooltip
                            placement="top"
                            title={t(
                                "views.home.components.tax_submissions_table.cell.check.tooltip",
                            )}
                        >
                            <Box>
                                <Checkbox disabled />
                            </Box>
                        </Tooltip>
                    ) : (
                        <Checkbox
                            checked={isRowSelected}
                            onChange={() =>
                                params.api.selectRow(
                                    params.row.id,
                                    !isRowSelected,
                                )
                            }
                        />
                    );
                },
            },
            {
                align: "left",
                field: "fulfilmentName",
                flex: 0.8,
                sortable: false,
                headerAlign: "left",
                headerName: t(
                    "views.home.components.tax_submissions_table.header.fulfilment",
                ),
                renderHeader: (params: GridColumnHeaderParams) =>
                    renderHeader(theme, params),
                renderCell: (params) => renderCell(theme, params),
            },
            {
                align: "left",
                field: "name",
                flex: isXlScreen ? 0.5 : 0.75,
                sortable: false,
                headerAlign: "left",
                headerName: t(
                    "views.home.components.tax_submissions_table.header.name",
                ),
                renderHeader: (params: GridColumnHeaderParams) =>
                    renderHeader(theme, params),
                renderCell: (params) => renderCell(theme, params),
            },
            {
                align: "left",
                field: "documents",
                flex: isXlScreen ? 0.2 : 0.4,
                sortable: false,
                headerAlign: "left",
                headerName: t(
                    "views.home.components.tax_submissions_table.header.documents",
                ),
                type: "number",
                renderHeader: (params: GridColumnHeaderParams) =>
                    renderHeader(theme, params, "pre-line"),
                renderCell: (params) => renderCell(theme, params),
            },
            {
                align: "left",
                field: "revoked",
                flex: isXlScreen ? 0.2 : 0.4,
                sortable: false,
                headerAlign: "left",
                headerName: t(
                    "views.home.components.tax_submissions_table.header.documents.revoked",
                ),
                type: "number",
                renderHeader: (params: GridColumnHeaderParams) =>
                    renderHeader(theme, params, "pre-line"),
                renderCell: (params) => renderCell(theme, params),
            },
            {
                align: "left",
                field: "deadline",
                flex: isXlScreen ? 0.2 : 0.5,
                sortable: true,
                headerAlign: "left",
                headerName: t(
                    "views.home.components.tax_submissions_table.header.deadline",
                ),
                renderHeader: (params: GridColumnHeaderParams) =>
                    renderHeader(theme, params),
                renderCell: (params) => renderDateCell(theme, params),
            },
            {
                align: "left",
                field: "sendDate",
                flex: isXlScreen ? 0.2 : 0.5,
                sortable: true,
                headerAlign: "left",
                headerName: t(
                    "views.home.components.tax_submissions_table.header.upload_date",
                ),
                renderHeader: (params: GridColumnHeaderParams) =>
                    renderHeader(theme, params),
                renderCell: (params) =>
                    params.value ? renderDateCell(theme, params) : "-",
            },
            {
                align: "left",
                field: "status",
                flex: isXlScreen ? 0.3 : 0.55,
                sortable: true,
                headerAlign: "left",
                headerName: t(
                    "views.home.components.tax_submissions_table.header.status",
                ),
                renderHeader: (params: GridColumnHeaderParams) =>
                    renderHeader(theme, params),
                renderCell: (params) => renderStatus(theme, params, t),
            },
            {
                field: "menu",
                type: "actions",
                flex: 0.1,
                getActions: getRowActions,
            },
            {
                align: "left",
                field: "actions",
                flex: isXlScreen ? 0.1 : 0.25,
                headerAlign: "left",
                hideSortIcons: true,
                renderCell: (params: GridRenderCellParams<RowModel>) =>
                    renderActionsCell(params, onClickTaxSubmission),
                sortable: false,
                type: "actions",
            },
        ],
        [t, theme, isXlScreen, getRowActions, onClickTaxSubmission],
    );
};
